import { isProductionSignupHost } from 'client/signup/utilities/environmentUtils';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import SignupConstants, { dataCenterRegions } from 'client/util/Constants/SignupConstants';
import axios from 'axios';

const { formInputFields } = SignupConstants;

export const noPersonalEmail = async (value) => {
  const lowercaseValue = value.toLowerCase();

  // Skip SendGrid domain validation outside production console hosts (WoK, staging, etc.)
  if (!isProductionSignupHost()) {
    return true;
  }

  const endpoint = `${window.location.origin}/api/v2/validatedomain?domain=${lowercaseValue}`;
  try {
    const response = await axios.get(endpoint);
    return response.data.message === 'Continue';
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);

    const fallbackPersonalEmailDomains = [
      'gmail.com',
      'yahoo.com',
      'hotmail.com',
      'comcast.net',
      'qq.com',
      'outlook.com',
      'icloud.com',
      'msn.com',
      'live.com',
      'tutanota.com',
      'posteo.de',
      'mailbox.com',
      'fastmail.com',
      'yandex.com',
      'gmx.com',
      'info.com',
      'mailinator.com',
      'protonmail.com',
      'proton.me',
      'pm.me',
      'protonmail.ch',
      'sharklasers.com',
    ];

    return fallbackPersonalEmailDomains.every((domain) => !lowercaseValue.endsWith(`@${domain}`));
  }
};

// Simple email validation rule for Confirm.vue
export const simpleEmailValidation = z.string({
  required_error: 'The email provided is invalid. Please try registering again.',
}).email('The email provided is invalid. Please try registering again.');

// Unicode allowlist for name/company fields. Blocks SQL/JS injection characters.
// Apostrophe is permitted for names like O'Brien and D'Angelo.
// Single hyphens allowed (e.g. Smith-Jones); double-dash (--) blocked via lookahead.
const SAFE_TEXT_REGEX = /^(?!.*--)[\p{L}\p{N} '\-.,&]+$/u;
const SAFE_TEXT_ERROR = 'This field contains invalid characters.';

// Field validation rules for OnePageForm.vue
const coreValidation = {
  [formInputFields.firstName.name]: z.string()
    .min(1, `The ${formInputFields.firstName.name} field is required`)
    .max(40, `The ${formInputFields.firstName.name} field may not be greater than 40 characters`)
    .regex(SAFE_TEXT_REGEX, SAFE_TEXT_ERROR),

  [formInputFields.lastName.name]: z.string()
    .min(1, `The ${formInputFields.lastName.name} field is required`)
    .max(40, `The ${formInputFields.lastName.name} field may not be greater than 40 characters`)
    .regex(SAFE_TEXT_REGEX, SAFE_TEXT_ERROR),

  [formInputFields.companyName.name]: z.string()
    .min(1, `The ${formInputFields.companyName.name} field is required`)
    .max(80, `The ${formInputFields.companyName.name} field may not be greater than 80 characters`)
    .regex(SAFE_TEXT_REGEX, SAFE_TEXT_ERROR),

  [formInputFields.email.name]: z.string()
    .min(1, `The ${formInputFields.email.name} field is required`)
    .email('Please provide a valid email address')
    .max(80, `The ${formInputFields.email.name} field may not be greater than 80 characters`),

  [formInputFields.country.name]: z.string()
    .min(1, `The ${formInputFields.country.name} field is required`),

  [formInputFields.state.name]: z.string().optional(),

  [formInputFields.dataCenter.name]: z.string().nullable().optional(),

  [formInputFields.phone.name]: z.string().optional(),

  [formInputFields.hasPhonePreference.name]: z.boolean().optional(),

  [formInputFields.termsOfService.name]: z.boolean()
    .refine(val => val === true, {
      message: `${formInputFields.termsOfService.label} is required`,
    }),
};

const standardSignupValidation = {
  [formInputFields.partner.name]: z.string()
    .min(1, `The ${formInputFields.partner.name} field is required`),

  [formInputFields.partnerType.name]: z.string().optional(),

  [formInputFields.companySize.name]: z.string()
    .min(1, `The ${formInputFields.companySize.name} field is required`),
};

const mspSignupValidation = {
  [formInputFields.endpointPotential.name]: z.string()
    .min(1, `The ${formInputFields.endpointPotential.name} field is required`),
};

// Field validation rules for ConfirmPartner.vue
const confirmPartnerValidation = {
  [formInputFields.partner.name]: z.string()
    .min(1, `The ${formInputFields.partner.name} field is required`),

  [formInputFields.partnerType.name]: z.string().optional(),

  [formInputFields.companySize.name]: z.string()
    .min(1, `The ${formInputFields.companySize.name} field is required`),

  [formInputFields.country.name]: z.string()
    .min(1, `The ${formInputFields.country.name} field is required`),
};

export const getFieldValidations = (isMsp) => {
  if (isMsp) {
    return {
      ...coreValidation,
      ...mspSignupValidation,
    };
  }
  return {
    ...coreValidation,
    ...standardSignupValidation,
  };
};

export const createValidationSchema = (isMsp) => {
  const fieldValidation = getFieldValidations(isMsp);

  return toTypedSchema(z.object(fieldValidation)
    .superRefine((data, ctx) => {
      // Partner validation - only if 'partner' value is 'Yes'
      if (
        !isMsp
        && data[formInputFields.partner.name]
        && data[formInputFields.partner.name] === 'Yes'
        && !data[formInputFields.partnerType.name]
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The ${formInputFields.partnerType.name} field is required`,
          path: [formInputFields.partnerType.name],
        });
      }

      // State validation - only if 'country' value has states
      if (
        data[formInputFields.country.name]
        && ['United States', 'Canada', 'Australia'].includes(data[formInputFields.country.name])
        && !data[formInputFields.state.name]
      ) {
        const country = data[formInputFields.country.name];
        if (['United States', 'Canada', 'Australia'].includes(country)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: country === 'Canada' ? 'The province field is required' : 'The state field is required',
            path: [formInputFields.state.name],
          });
        }
      }

      // Phone validation - only if 'hasPhonePreference' value is true
      if (
        data[formInputFields.hasPhonePreference.name]
        && (!data[formInputFields.phone.name] || data[formInputFields.phone.name].trim() === '')
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `The ${formInputFields.phone.name} field is required`,
          path: [formInputFields.phone.name],
        });
      }

      // Data center validation - only if country has regional data center options
      if (data[formInputFields.country.name]) {
        const country = data[formInputFields.country.name];

        // Check if the country has regional data center options (non-US options available)
        const hasRegionalOptions = Object.values(dataCenterRegions).some(
          ({ countries }) => countries && countries.includes(country),
        );

        if (hasRegionalOptions && !data[formInputFields.dataCenter.name]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'The data storage field is required',
            path: [formInputFields.dataCenter.name],
          });
        }
      }
    }));
};

export const createPartnerValidationSchema = () => toTypedSchema(z.object(confirmPartnerValidation)
  .superRefine((data, ctx) => {
    // Partner validation - only if 'partner' value is 'Yes'
    if (
      data[formInputFields.partner.name]
      && data[formInputFields.partner.name] === 'Yes'
      && !data[formInputFields.partnerType.name]
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The ${formInputFields.partnerType.name} field is required`,
        path: [formInputFields.partnerType.name],
      });
    }
  }));
